import { useEffect, type RefObject } from "react";

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), "
  + "select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

// Focus for an in-place full-screen surface (the matching workspace, a grid's full
// screen). Focus moves inside when it opens, preferring its first search box, and returns
// to where it was when it closes; Tab cycles within it. Without this, focus stayed on the
// button that opened it, now BEHIND the surface, so Esc went to that button's section
// header and Tab walked the hidden page. The surface itself needs tabIndex={-1}, as the
// fallback target when it holds nothing focusable.
export function useDialogFocus(active: boolean, ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const box = ref.current;
    if (!active || !box) return;
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () => [...box.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((el) => el.getClientRects().length > 0);
    const search = box.querySelector<HTMLElement>(
      "textarea:not([disabled]), input:not([disabled]):not([type=checkbox]):not([type=radio])");
    (search ?? focusables()[0] ?? box).focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || e.defaultPrevented) return;
      // A surface nested inside this one (a grid's full screen in the workspace) owns Tab.
      if ((e.target as Element | null)?.closest("[aria-modal='true']") !== box) return;
      const els = focusables();
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    box.addEventListener("keydown", onKey);
    return () => {
      box.removeEventListener("keydown", onKey);
      if (prev?.isConnected) prev.focus();
    };
  }, [active, ref]);
}
